/**
 * Cleanup-and-reload primitive. ALL hard reloads MUST go through
 * `cleanCachesAndReload`. Best-effort cleanup with strict timeouts; the
 * reload itself is guaranteed even if caches / SWs hang.
 *
 * Storage preservation rules live in `./constants` (PRESERVE_PREFIXES /
 * PRESERVE_EXACT) — notably `sb-*` (Supabase auth) and `current_session_id`
 * (single-device guard) must always survive a version reload.
 */

import {
  safeGetSessionStorage,
  safeSetSessionStorage,
  safeSetLocalStorage,
} from "@/lib/safe-storage";
import {
  CLEANUP_CEILING_MS,
  PRESERVE_EXACT,
  PRESERVE_PREFIXES,
  RELOAD_TARGET_KEY,
  VERSION_RELOAD_GUARD_KEY,
} from "./constants";
import { withTimeout } from "./manifest";
import { writeReloadTarget } from "./reload-target";
import type { WatcherState } from "./types";

export function clearAppLocalStorage(): number {
  let removed = 0;
  try {
    const ls = window.localStorage;
    const keys: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k) keys.push(k);
    }
    for (const k of keys) {
      if (PRESERVE_EXACT.has(k)) continue;
      if (PRESERVE_PREFIXES.some((p) => k.startsWith(p))) continue;
      try {
        ls.removeItem(k);
        removed++;
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
  return removed;
}

export async function cleanCachesAndReload(opts: {
  reason: string;
  redirectPath?: string;
  reloadTarget?: string | null;
  triggeredBy?: WatcherState["reloadTriggeredBy"];
}): Promise<void> {
  console.log("[VersionWatcher] cleanCachesAndReload", opts);

  if (opts.reloadTarget) {
    writeReloadTarget(opts.reloadTarget);
  }

  const jobs: Promise<unknown>[] = [];
  if (typeof window !== "undefined" && "caches" in window) {
    jobs.push(
      withTimeout(
        (async () => {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        })(),
        CLEANUP_CEILING_MS,
        "caches"
      )
    );
  }
  if ("serviceWorker" in navigator) {
    jobs.push(
      withTimeout(
        (async () => {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        })(),
        CLEANUP_CEILING_MS,
        "sw-unregister"
      )
    );
  }
  try {
    // Preserve only the reload-target sentinel and triggered-by marker in
    // sessionStorage. The 30-min reload guard now lives in localStorage so
    // it survives PWA cold-starts (sessionStorage clears on app reopen,
    // which made the "Nueva versión disponible" banner reappear every launch).
    const target = safeGetSessionStorage(RELOAD_TARGET_KEY);
    const triggeredBy = safeGetSessionStorage("mblearn_reload_triggered_by");
    sessionStorage.clear();
    if (target) safeSetSessionStorage(RELOAD_TARGET_KEY, target);
    if (triggeredBy) safeSetSessionStorage("mblearn_reload_triggered_by", triggeredBy);
  } catch {
    /* ignore */
  }
  try {
    safeSetSessionStorage("mblearn_reload_triggered_by", opts.triggeredBy || "unknown");
    // Persistent cooldown across PWA cold-starts.
    safeSetLocalStorage(VERSION_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  clearAppLocalStorage();

  await Promise.race([
    Promise.all(jobs),
    new Promise((resolve) => setTimeout(resolve, CLEANUP_CEILING_MS)),
  ]);

  try {
    const base = opts.redirectPath || window.location.pathname + window.location.search;
    const url = new URL(base, window.location.origin);
    url.searchParams.delete("updated");
    url.searchParams.set("fresh", String(Date.now()));
    url.searchParams.set("updatedBy", opts.triggeredBy || "unknown");
    window.location.replace(url.toString());
  } catch {
    try {
      window.location.reload();
    } catch {
      /* nothing left to do */
    }
  }
}
